import React, {useEffect, useMemo, useState} from "react";
import {withRouter} from "react-router-dom";
import {useFiltered} from "@atttomyx/shared-hooks";
import {CircularProgress, IconButton, Typography} from "@mui/material";
import {ContentCopy as CloneIcon, Delete as DeleteIcon, Edit as EditIcon,} from "@mui/icons-material";
import {Cards, ConfirmDeleteDialog, FiltersAccordion, FloatingAddButton} from "@atttomyx/react-components";
import {CourseCard, DocumentsCard} from "../cards";
import {AddCourseDialog} from "../dialogs";
import {CourseFilters} from "../filters";
import {courseService} from "../../services";
import {arrays, objects, sorting} from "@atttomyx/shared-utils";
import {confirm, router} from "@atttomyx/react-utils";
import {PAGE_COURSE, TEMPLATE_ID_BUSINESS_PLAN, TEMPLATE_ID_PITCH_DECK} from "../../constants";
import "./coursesPage.css";

const CoursesPage = (props) => {
    const { history, snackbar, dimensions, user, courses, documents, videos, upsells, questionnaires, questions, submissions, files, progress, filters,
        onSaveCourse, onSaveSubmission, onSaveFiles, onSaveProgress, onDelete } = props;
    const [ course, setCourse ] = useState(null);
    const [ cloning, setCloning ] = useState(null);
    const [ showAdd, setShowAdd ] = useState(false);
    const [ showDelete, setShowDelete ] = useState(false);
    const [ showFilters, setShowFilters ] = useState(false);

    const available = useMemo(() => {
        const rank = user.settings.rank;

        return user.roles.inventor
            ? courses.filter(course => arrays.contains(course.ranks, rank))
            : courses;
    }, [user, courses]);

    const filtered = useFiltered(available, filters, sorting.sortByOrder);

    const nextOrder = useMemo(() => {
        let next = courses.length;

        courses.forEach(course => {
            if (course.order >= next) {
                next = course.order + 1;
            }
        });

        return next;
    }, [courses]);

    const documentsById = useMemo(() => {
        return arrays.getIdToEntity(documents);
    }, [documents]);

    const videosById = useMemo(() => {
        return arrays.getIdToEntity(videos);
    }, [videos]);

    const upsellsById = useMemo(() => {
        return arrays.getIdToEntity(upsells);
    }, [upsells]);

    const questionnairesById = useMemo(() => {
        return arrays.getIdToEntity(questionnaires);
    }, [questionnaires]);

    const questionsByQuestionnaireId = useMemo(() => {
        const map = {};

        questions.forEach(question => {
            const array = objects.defaultIfNullOrUndefined(map[question.questionnaireId], []);

            arrays.addTo(array, question);
            map[question.questionnaireId] = array;
        });

        return map;
    }, [questions]);

    const submissionsByQuestionnaireId = useMemo(() => {
        const map = {};

        submissions.forEach(submission => {
            const questionnaireId = submission.questionnaireId;

            map[questionnaireId] = submission;
        });

        return map;
    }, [submissions]);

    const filesByCourseId = useMemo(() => {
        const fileByTemplateId = {};
        const map = {};

        files.forEach(file => {
            const templateId = file.templateId;

            if (templateId) {
                fileByTemplateId[templateId] = file;
            }
        });

        courses.forEach(course => {
            const courseId = course.id;
            const templateIds = []

            arrays.addAll(templateIds, course.templateIds);
            arrays.addTo(templateIds, TEMPLATE_ID_BUSINESS_PLAN);
            arrays.addTo(templateIds, TEMPLATE_ID_PITCH_DECK);

            templateIds.forEach(templateId => {
                const file = fileByTemplateId[templateId];

                if (file) {
                    const array = objects.defaultIfNullOrUndefined(map[courseId], []);

                    arrays.addTo(array, file);
                    map[courseId] = array;
                }
            });
        });

        return map;
    }, [courses, files]);

    const Card = useMemo(() => {
        return user.roles.admin ? CourseCard : DocumentsCard;
    }, [user]);

    useEffect(() => {
        if (!course) {
            setShowAdd(false);
            setShowDelete(false);
        }
    }, [course]);

    useEffect(() => {
        if (cloning) {
            const success = (copy) => {
                snackbar.setSuccess("Module copied");
                setCloning(null);
                onSaveCourse(copy);
            };

            const failure = (err) => {
                snackbar.setError(err);
                setCloning(null);
            };

            courseService.cloneCourse(cloning.id, success, failure);
        }
    }, [cloning]);

    return <div className="courses-page">
        <Typography variant="h5" paragraph={!user.roles.inventor}>
            Modules
        </Typography>
        {user.roles.inventor ?
            <Typography variant="caption">
                We recommend that you complete each module in order.
            </Typography> : null}
        {user.roles.admin ?
            <div className="field">
                <FiltersAccordion
                    filters={filters}
                    form={CourseFilters}
                    open={showFilters}
                    onOpen={() => setShowFilters(true)}
                    onClose={() => setShowFilters(false)}
                    formProps={{
                        user: user,
                    }}
                />
            </div> : null}
        <Cards
            items={filtered}
            paging={filters.paging}
            dimensions={dimensions}
            renderer={(course) =>
                <Card
                    key={course.id}
                    snackbar={snackbar}
                    user={user}
                    course={course}
                    documentsById={documentsById}
                    videosById={videosById}
                    upsellsById={upsellsById}
                    questionnairesById={questionnairesById}
                    questionsByQuestionnaireId={questionsByQuestionnaireId}
                    submissionsByQuestionnaireId={submissionsByQuestionnaireId}
                    filesByCourseId={filesByCourseId}
                    progress={progress}
                    filters={filters}
                    onSaveSubmission={onSaveSubmission}
                    onSaveFiles={onSaveFiles}
                    onSaveProgress={onSaveProgress}
                >
                    {user.roles.admin ?
                        <>
                            <IconButton color="secondary" title="Delete" className="delete"
                                        onClick={() => {
                                            setCourse(course);
                                            setShowDelete(true);
                                        }}>
                                <DeleteIcon/>
                            </IconButton>
                            {cloning && cloning.id === course.id ?
                                <CircularProgress size="18px"/> :
                                <IconButton color="primary" title="Copy"
                                            onClick={() => confirm.confirm(
                                                `Are you sure you want to make a copy of ${course.name}?`,
                                                () => setCloning(course))}>
                                    <CloneIcon/>
                                </IconButton>}
                            <IconButton color="primary" title="Edit"
                                        onClick={() => router.redirectTo(history, PAGE_COURSE + "/" + course.id)}>
                                <EditIcon/>
                            </IconButton>
                        </> : null}
                </Card>}
        />
        {user.roles.admin ?
            <FloatingAddButton
                title="Add course"
                position={dimensions.portrait ? "higher" : undefined}
                center={false}
                onClick={() => {
                    setShowAdd(true);
                }}
            /> : null}
        {showAdd ?
            <AddCourseDialog
                snackbar={snackbar}
                order={nextOrder}
                onCancel={() => {
                    setShowAdd(false);
                }}
                onSave={(saved) => {
                    setShowAdd(false);
                    onSaveCourse(saved);
                }}
            /> : null}
        {showDelete && course ?
            <ConfirmDeleteDialog
                snackbar={snackbar}
                title={course.name}
                onCancel={() => {
                    setShowDelete(false);
                    setCourse(null);
                }}
                onDelete={(courseId) => {
                    setShowDelete(false);
                    setCourse(null);
                    onDelete(courseId);
                }}
                deleter={(success, failure) => courseService.deleteCourse(course.id, success, failure)}
            /> : null}
    </div>
}

export default withRouter(CoursesPage);
